import produce from 'immer';

import { selectOptionFactory } from '../../utils';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';

import { DELETE_PROJECTS, FETCH_PROJECT_BRANCHES, FETCH_PROJECT_REVISIONS, FETCH_PROJECTS } from './actions';
import type { ProjectsStore } from './store';

const initialState: ProjectsStore = {
  projects: {},
  status: getInitialStatus(),
  selectedProjectIds: [],
};

export default createReducer(initialState, {
  [`${FETCH_PROJECTS}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${FETCH_PROJECTS}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    (action.payload.data || []).forEach((project) => {
      if (project?.locator) {
        draftState.projects[project.locator] = project;
      }
    });
  }),
  [`${FETCH_PROJECTS}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),
  [`${DELETE_PROJECTS}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${DELETE_PROJECTS}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    action.meta.locators.forEach((loc) => delete draftState.projects[loc]);
  }),
  [`${DELETE_PROJECTS}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),
  [`${FETCH_PROJECT_BRANCHES}_PENDING`]: (state: ProjectsStore, action) => produce(state, draftState => {
    const { locator } = action.meta;
    const project = state.projects[locator];
    draftState.projects[locator] = {
      ...project,
      locator,
      isFetchingBranches: true,
    };
    if (project?.default_branch) {
      draftState.projects[locator].selectedBranch = selectOptionFactory(project.default_branch);
    }
  }),
  [`${FETCH_PROJECT_BRANCHES}_FULFILLED`]: (state: ProjectsStore, action) => produce(state, draftState => {
    const { data } = action.payload;
    const { locator, references } = data;
    const project = state.projects[locator];
    const findFirstBranch = branch => branch.type === 'branch';
    const selectedBranch = project.default_branch || references.find(findFirstBranch).name;
    draftState.projects[locator] = {
      ...project,
      locator,
      selectedBranch: selectOptionFactory(selectedBranch),
      references: references.sort((a, b) => a.name?.localeCompare(b.name)),
      isFetchingBranches: false,
    };
  }),
  [`${FETCH_PROJECT_REVISIONS}_PENDING`]: (state: ProjectsStore, action) => produce(state, draftState => {
    const { locator } = action.meta;
    const project = state.projects[locator];
    draftState.projects[locator] = {
      ...project,
      isFetchingRevisions: true,
    };
  }),
  [`${FETCH_PROJECT_REVISIONS}_FULFILLED`]: (state: ProjectsStore, action) => produce(state, draftState => {
    const { data } = action.payload;
    const { branch, locator } = action.meta;
    const project = state.projects[locator];
    draftState.projects[locator] = {
      ...project,
      selectedRevision: null, // null === 'latest'
      revisions: {
        [branch]: data.branch[branch],
      },
      isFetchingRevisions: false,
    };
  }),
});
